import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPanelModal } from './AdminPanelModal';
import { loadAdminConfig, loadAdminConfigAsync, AdminStoreConfig } from './data/adminStore';
import { SaveResumeModal } from './SaveResumeModal';

export const Launcher: React.FC = () => {
  const navigate = useNavigate();
  const [adminConfig, setAdminConfig] = useState<AdminStoreConfig>(loadAdminConfig);
  const [clickCount, setClickCount] = useState(0);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  useEffect(() => {
    loadAdminConfigAsync().then((conf) => {
      setAdminConfig(conf);
    });

    const handleSync = () => {
      setAdminConfig(loadAdminConfig());
    };
    window.addEventListener('adminConfigUpdated', handleSync);
    return () => window.removeEventListener('adminConfigUpdated', handleSync);
  }, []);

  const handleHeaderBadgeClick = () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

    const nextCount = clickCount + 1;
    setClickCount(nextCount);

    if (nextCount >= 6) {
      setShowAuthModal(true);
      setClickCount(0);
    } else {
      clickTimerRef.current = setTimeout(() => {
        setClickCount(0);
      }, 3000);
    }
  };

  const handleAuthSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === 'mySky@2025') {
      setShowAuthModal(false);
      setPasswordInput('');
      setAuthError(false);
      setShowAdminModal(true);
    } else {
      setAuthError(true);
    }
  };

  return (
    <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '2rem 1rem 5rem' }}>
      {/* HERO BRANDING HEADER */}
      <header
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #2563eb 100%)',
          borderRadius: '20px',
          padding: '2.5rem 2rem',
          color: '#ffffff',
          boxShadow: '0 15px 35px -5px rgba(30, 58, 138, 0.35)',
          marginBottom: '2.5rem',
          textAlign: 'center',
        }}
      >
        <span
          onClick={handleHeaderBadgeClick}
          style={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(8px)',
            padding: '0.4rem 1rem',
            borderRadius: '9999px',
            fontSize: '0.8rem',
            fontWeight: '700',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            userSelect: 'none',
            display: 'inline-block',
          }}
        >
          ✈️ Global e-Visa Application Portal
        </span>

        <h1 style={{ fontSize: '2.4rem', fontWeight: '800', marginTop: '0.75rem', marginBottom: '0.35rem' }}>
          mySky Travels e-Visa Hub
        </h1>
        <p style={{ color: '#93c5fd', fontSize: '1rem', opacity: 0.95, margin: '0 0 1.25rem' }}>
          Exclusively designed for <strong>mySky Travels</strong> by{' '}
          <a
            href="https://www.madushka.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#ffffff', fontWeight: '700', textDecoration: 'underline' }}
          >
            Madushka Athapaththu
          </a>
        </p>

        <button
          type="button"
          onClick={() => setShowResumeModal(true)}
          style={{
            padding: '0.65rem 1.25rem',
            borderRadius: '9999px',
            border: 'none',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            color: '#ffffff',
            fontWeight: '700',
            fontSize: '0.875rem',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          🔑 Resume Saved Application (Enter 6-Digit Code)
        </button>
      </header>

      {/* DESTINATIONS GRID */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#0f172a', marginBottom: '1.25rem' }}>
          🌐 Select e-Visa Destination
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {adminConfig.helpers
            .sort((a, b) => a.order - b.order)
            .map((dest) => (
              <div
                key={dest.id}
                onClick={() => {
                  if (dest.isLocked) {
                    alert('🔒 This e-Visa helper is currently locked by the agent.');
                    return;
                  }
                  navigate(dest.path);
                }}
                style={{
                  background: '#ffffff',
                  borderRadius: '16px',
                  border: dest.isLocked ? '2px solid #ef4444' : '1px solid #e2e8f0',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                  cursor: dest.isLocked ? 'not-allowed' : 'pointer',
                  transition: 'all 0.25s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  opacity: dest.isLocked ? 0.75 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!dest.isLocked) {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 20px 30px -10px rgba(37, 99, 235, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!dest.isLocked) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.08)';
                  }
                }}
              >
                {/* Card Banner */}
                <div
                  style={{
                    background: dest.bgGradient,
                    padding: '1.75rem 1.5rem',
                    color: '#ffffff',
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '1.25rem',
                      right: '1.25rem',
                      background: dest.isLocked ? '#dc2626' : dest.badgeBg,
                      color: '#ffffff',
                      padding: '0.25rem 0.65rem',
                      borderRadius: '9999px',
                      fontSize: '0.725rem',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                    }}
                  >
                    {dest.isLocked ? '🔒 Locked' : dest.badge}
                  </span>

                  <div style={{ fontSize: '3rem', lineHeight: 1, marginBottom: '0.5rem' }}>{dest.flag}</div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>{dest.country} e-Visa</h3>
                  <div style={{ fontSize: '0.825rem', color: 'rgba(255,255,255,0.85)', marginTop: '0.2rem' }}>
                    {dest.category}
                  </div>
                </div>

                {/* Card Details */}
                <div style={{ padding: '1.25rem 1.5rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '0.5rem' }}>
                      <strong>Available Visa Types:</strong>
                      <div style={{ color: '#0f172a', fontWeight: '600', marginTop: '0.2rem' }}>{dest.types}</div>
                    </div>

                    <div style={{ fontSize: '0.825rem', color: '#64748b', marginBottom: '1.25rem' }}>
                      ⏱️ Processing Time: <strong>{dest.processingTime}</strong>
                    </div>
                  </div>

                  <button
                    type="button"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      borderRadius: '10px',
                      border: 'none',
                      background: dest.isLocked ? '#fef2f2' : '#eff6ff',
                      color: dest.isLocked ? '#dc2626' : '#2563eb',
                      fontWeight: '700',
                      fontSize: '0.925rem',
                      cursor: dest.isLocked ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {dest.isLocked ? '🔒 Helper Locked by Agent' : `Start ${dest.country} Application →`}
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* PASSWORD AUTHENTICATION MODAL */}
      {showAuthModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem',
          }}
        >
          <form
            onSubmit={handleAuthSubmit}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.35rem' }}>
              🔒 Secret Admin Authentication
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.25rem' }}>
              Enter master password to access the hidden Admin Control Panel:
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <input
                type="password"
                placeholder="Enter Password..."
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setAuthError(false);
                }}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
              {authError && (
                <div style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: '600', marginTop: '0.35rem' }}>
                  ❌ Incorrect Password. Please try again.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowAuthModal(false);
                  setPasswordInput('');
                  setAuthError(false);
                }}
                className="btn-secondary"
                style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" style={{ padding: '0.5rem 1.25rem', fontSize: '0.85rem' }}>
                Unlock Admin Panel ⚡
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FULL ADMIN CONTROL PANEL MODAL */}
      {showAdminModal && (
        <AdminPanelModal onClose={() => setShowAdminModal(false)} />
      )}

      {showResumeModal && (
        <SaveResumeModal
          mode="RESUME"
          destination="Global"
          onClose={() => setShowResumeModal(false)}
          onRestore={(saved) => {
            if (saved.destination) {
              const destLower = saved.destination.toLowerCase();
              if (destLower.includes('india')) navigate(`/india?resume=${saved.code}`);
              else if (destLower.includes('malaysia')) navigate(`/malaysia?resume=${saved.code}`);
              else if (destLower.includes('vietnam')) navigate(`/vietnam?resume=${saved.code}`);
              else navigate(`/${destLower}?resume=${saved.code}`);
            }
          }}
        />
      )}
    </div>
  );
};
